/**
 * Permission utility functions for role-based access control
 * Supports actions: 'create', 'edit', 'view', 'delete'
 */

interface User {
  username?: string;
  role?: string;
  team?: string;
  actions?: string[];
  action?: string[]; // Support both 'actions' and 'action' from backend
  [key: string]: any;
}

/**
 * Check if the current user has permission to perform a specific action
 * @param user - User object from AuthContext
 * @param action - Action to check: 'create', 'edit', 'view', or 'delete'
 * @returns boolean indicating if user has permission
 */
export const hasPermission = (user: User | null | undefined, action: string): boolean => {
  if (!user) {
    return false;
  }

  // Get actions array (support both 'actions' and 'action' field names)
  const userActions = user.actions || user.action || [];

  // Check if the action exists in user's permissions
  return userActions.includes(action);
};

/**
 * Check if user can create projects
 */
export const canCreateProject = (user: User | null | undefined): boolean => {
  return hasPermission(user, 'create');
};

/**
 * Check if user can edit/update projects
 */
export const canEditProject = (user: User | null | undefined): boolean => {
  return hasPermission(user, 'edit');
};

/**
 * Check if user can view projects
 */
export const canViewProject = (user: User | null | undefined): boolean => {
  return hasPermission(user, 'view');
};

/**
 * Check if user can delete projects
 */
export const canDeleteProject = (user: User | null | undefined): boolean => {
  return hasPermission(user, 'delete');
};

/**
 * Get a user-friendly role display name
 */
export const getRoleDisplayName = (role: string | undefined): string => {
  if (!role) return 'User';
  
  const roleMap: Record<string, string> = {
    'admin': 'Administrator',
    'developer': 'Developer',
    'readonly': 'Read Only'
  };

  return roleMap[role.toLowerCase()] || role;
};

/**
 * Check if user is an admin
 */
export const isAdmin = (user: User | null | undefined): boolean => {
  return user?.role?.toLowerCase() === 'admin';
};

/**
 * Check if user is a developer
 */
export const isDeveloper = (user: User | null | undefined): boolean => {
  return user?.role?.toLowerCase() === 'developer';
};

/**
 * Check if user is read-only
 */
export const isReadOnly = (user: User | null | undefined): boolean => {
  return user?.role?.toLowerCase() === 'readonly';
};
